import dayjs from 'dayjs';

import { DATE } from '@cast/constants';
import {
  Card,
  Icons,
  LabelChip,
  ProgressBar,
  ShadowDivider,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import type { BillingV1FeatureUsageDetail } from '@cast/types';

type DetailOverviewCardProps = {
  detail: BillingV1FeatureUsageDetail;
};

export const DetailOverviewCard = ({ detail }: DetailOverviewCardProps) => {
  const credits = detail.credits;
  const freeOfCharge = detail.freeOfCharge;
  const unit = detail.unit ?? '';

  const progressValue =
    credits?.total && credits.total > 0
      ? ((credits.used ?? 0) / credits.total) * 100
      : 0;

  const percentUsed =
    credits?.total && credits.total > 0
      ? Math.round(((credits.used ?? 0) / credits.total) * 100)
      : 0;

  const periodStart = credits?.startDate
    ? dayjs(credits.startDate).format('MM/DD/YYYY')
    : '–';
  const periodEnd = credits?.endDate
    ? dayjs(credits.endDate).format('MM/DD/YYYY')
    : '–';

  return (
    <Stack gap={16}>
      <Typography color={tokens.theme.fg.structural.primary} variant="h6">
        Current month
      </Typography>
      <Stack direction="row" gap={16} alignItems="stretch">
        {/* Card 1: current month usage */}
        <Card
          sx={{
            flex: '1 0 0',
            p: 16,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Stack gap={4}>
            <Typography
              variant="L10R"
              color={tokens.theme.fg.structural.secondary}
              sx={{ textTransform: 'uppercase' }}
            >
              Current month usage
            </Typography>
            <Stack direction="row" alignItems="baseline" gap={8}>
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="h3"
                data-testid="detail-total-usage"
              >
                {(detail.totalUsage ?? 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography
                variant="L10R"
                color={tokens.theme.fg.structural.secondary}
                sx={{ textTransform: 'uppercase' }}
              >
                {unit}
              </Typography>
            </Stack>
          </Stack>

          {freeOfCharge?.isFreeOfCharge && (
            <Stack direction="row" alignItems="center" gap={8}>
              <LabelChip
                size="xs"
                sx={{
                  bgcolor: tokens.theme.bg.status.successStrong,
                  color: tokens.theme.fg.structural.primaryOnInverse,
                }}
                endIcon={
                  <Tooltip title="Usage is not billed and does not count against your credits until the date shown.">
                    <Box component="span" sx={{ display: 'flex' }}>
                      <Icons.Info size={10} />
                    </Box>
                  </Tooltip>
                }
              >
                Free of charge
              </LabelChip>
              {freeOfCharge.endDate && (
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  Until {dayjs(freeOfCharge.endDate).format(DATE)}
                </Typography>
              )}
            </Stack>
          )}
        </Card>

        {/* Card 2: credits + period/credit reset */}
        {credits?.total && credits.total > 0 && (
          <Card
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              overflow: 'hidden',
              flexShrink: 0,
              width: 840,
              paddingX: 0,
            }}
          >
            {/* Credits section */}
            <Stack
              gap={4}
              sx={{
                flex: '1 0 0',
                p: 16,
                minWidth: 0,
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="L10R"
                color={tokens.theme.fg.structural.secondary}
                sx={{ textTransform: 'uppercase' }}
              >
                Credits
              </Typography>
              <Stack direction="row" alignItems="baseline" gap={4}>
                <Typography
                  color={tokens.theme.fg.structural.primary}
                  variant="h3"
                >
                  {percentUsed}%
                </Typography>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  used
                </Typography>
              </Stack>
              <Stack gap={4}>
                <ProgressBar
                  size="lg"
                  variant="determinate"
                  value={progressValue}
                />
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography
                      color={tokens.theme.fg.structural.primary}
                      variant="P12B"
                    >
                      {Math.round(credits.used ?? 0).toLocaleString('en-US')} /{' '}
                      {Math.round(credits.total).toLocaleString('en-US')}
                    </Typography>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {unit} used
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography
                      color={tokens.theme.fg.structural.primary}
                      variant="P12B"
                    >
                      {Math.round(credits.remaining ?? 0).toLocaleString(
                        'en-US'
                      )}
                    </Typography>
                    <Typography
                      variant="P12R"
                      color={tokens.theme.fg.structural.secondary}
                    >
                      {unit} remaining
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            {/* Period / credit reset section */}
            <Box
              sx={{
                width: 277,
                flexShrink: 0,
                pl: 24,
                pr: 16,
                py: 16,
                bgcolor: tokens.theme.bg.structural.surfaceSubtle,
                position: 'relative',
                display: 'flex',
                alignItems: 'stretch',
              }}
            >
              <ShadowDivider
                orientation="vertical"
                direction="right"
                absolute
                left
                top
                bottom
                size={16}
                opacity={0.06}
              />
              <Stack justifyContent="space-between" sx={{ width: '100%' }}>
                <Stack gap={4}>
                  <Typography
                    variant="L10R"
                    color={tokens.theme.fg.structural.secondary}
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Credit period
                  </Typography>
                  <Typography
                    color={tokens.theme.fg.structural.primary}
                    variant="P12M"
                  >
                    {periodStart} to {periodEnd}
                  </Typography>
                </Stack>
                <Stack gap={4}>
                  <Stack direction="row" alignItems="center" gap={4}>
                    <Typography
                      variant="L10R"
                      color={tokens.theme.fg.structural.secondary}
                      sx={{ textTransform: 'uppercase' }}
                    >
                      Credit reset
                    </Typography>
                    <Tooltip title="How often your credit allocation renews. Monthly credits reset on the 1st of each month. Custom credits are fixed for the full period.">
                      <Box component="span" sx={{ display: 'flex' }}>
                        <Icons.Info size={14} />
                      </Box>
                    </Tooltip>
                  </Stack>
                  <Typography
                    color={tokens.theme.fg.structural.primary}
                    variant="P12B"
                  >
                    {credits?.resetFrequency
                      ? credits.resetFrequency.charAt(0).toUpperCase() +
                        credits.resetFrequency.slice(1)
                      : '–'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Card>
        )}
      </Stack>
    </Stack>
  );
};
